/**
 * A reporting seam for code that must not depend on the error reporter itself.
 *
 * Globally registered components are the case this exists for. <Icon> is registered
 * in main.js, swingu.js and demo.swingu.js, but only main.js calls Sentry.init() —
 * so importing @sentry/vue inside <Icon> put ~6 KiB (gzip) of SDK into the two
 * entrypoints where captureException is a no-op and can never report anything.
 *
 * Importing the SDK lazily on the failure path solves that but splits Sentry into
 * its own chunk, which costs every entrypoint that *does* use it about a kilobyte of
 * chunk and preload overhead. Injecting the reporter avoids both: the SDK stays
 * statically bundled only where it is initialised, and everywhere else this module
 * is a few bytes that do nothing.
 *
 * Whoever initialises reporting calls setErrorReporter() once; callers just call
 * reportError() and neither knows nor cares whether anything is listening.
 */

/**
 * @typedef {object} ReportContext
 * @property {Record<string, string>} [tags]  Searchable key/values on the report.
 */

/** @typedef {(error: unknown, context?: ReportContext) => void} ErrorReporter */

/** @type {ErrorReporter | null} */
let reporter = null;

/**
 * Register the reporter. Called once during app setup, after the underlying SDK is
 * initialised — see main.js. Entrypoints with no error reporting simply never call
 * this, which leaves reportError() a no-op.
 *
 * @param {ErrorReporter} report
 */
export function setErrorReporter(report) {
    reporter = report;
}

/**
 * Report a handled error, if anything is listening.
 *
 * Never throws. Callers are in catch blocks by definition, and a reporter that threw
 * would turn a handled failure back into an unhandled rejection — which for webview
 * chunk failures is the whole problem being avoided (CADDIE-3439).
 *
 * @param {unknown} error
 * @param {ReportContext} [context]
 */
export function reportError(error, context) {
    if (!reporter) {
        return;
    }

    try {
        reporter(error, context);
    } catch {
        // Reporting is best-effort; losing a report beats breaking the caller.
    }
}
